// WS-18: the tree texture atlas, drawn procedurally (no downloaded assets, nothing to license).
// Worker-safe: no THREE and no DOM; the caller passes a canvas factory (OffscreenCanvas in the worker,
// a DOM canvas on the main thread). Used by web/js/trees.js (prepareAtlas) and trees-atlas-worker.js.
export const mulberry = (a) => () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
const lerp = (a, b, t) => a + (b - a) * t;
const clamp01 = (x) => Math.min(1, Math.max(0, x));
export const AUX = 512;
// ------------------------------------------------------------------ 1. texture atlas (1024 px, canvas, sRGB)
// Regions in atlas pixels [x, y, w, h]; canvas y down = texture v up (DataTexture, no flip).
export const AT = 1024;
export const REG = {
  pine: [8, 8, 496, 496],        // needle tuft, top view; long axis = u (twig base at u0, tip at u1)
  spruce: [520, 8, 496, 496],    // spray (frond), top view; same convention
  birch: [8, 520, 496, 496],     // twig fan, side view; base at v0 (bottom of the card)
  pineBark: [520, 520, 120, 496],   // v0 = ground, v1 = top
  birchBark: [648, 520, 120, 496],
  spruceBark: [776, 520, 56, 496],
  core: [840, 520, 88, 88],      // dark needle interior (opaque), spruce core
  solid: [936, 520, 80, 80],     // opaque white (stylised pads: vertex colour), snow settle mask = soft noise
  solidFlat: [840, 616, 88, 88], // opaque white, no snow (stylised trunks)
};
export const uvRect = (r) => ({ u0: r[0] / AT, v0: r[1] / AT, u1: (r[0] + r[2]) / AT, v1: (r[1] + r[3]) / AT });

/** Draw the atlas pair. Returns { map, aux } DataTextures (map sRGB with straight alpha, aux linear). */

/** Draw the atlas pair. Returns { map (AT² RGBA, sRGB, straight alpha), aux (AUX² RGBA: R snow settle, G leaf mask, B AO) }.
 *  opt.birch = vegetation.trees.hq.birch (WS-76 birch round 3, preview-gated; absent = today's atlas, pixel for pixel):
 *  fans 'fine' = finer, lighter grey-brown twig fans (a hint of purple-red) grown evenly along three shoots (real coverage, no dark blobs);
 *  foot = share of the stem height that is the darker foot, grey-brown (default: 0.14, dark grey);
 *  leaves = { size, density, spread (envelope width x) } (with fans 'fine'): rounded leaf clusters with gaps instead of the solid blob mask (aux G = cluster
 *  rank for the season's thinning, aux A = leaf shade; trees.js uLeafHq). */
export function drawAtlas(newCanvas, opt = {}) {
  const OB = opt.birch || {}, FINE = OB.fans === 'fine', LV = FINE && OB.leaves ? OB.leaves : null, RL = mulberry(97); // LV: WS-76 birch leaves
  const cv = newCanvas(AT, AT);
  const g = cv.getContext('2d', { willReadFrequently: true });
  const R = mulberry(41);
  // aux channels are drawn as separate greyscale canvases, then blurred in JS (ctx.filter is not on all Safaris)
  const mk = () => newCanvas(AT, AT).getContext('2d', { willReadFrequently: true });
  const snowC = mk(), leafC = mk(), aoC = mk(), shadeC = LV ? mk() : null; // shadeC: WS-76 leaf shade (aux A)
  for (const c of [snowC, leafC]) { c.fillStyle = '#000'; c.fillRect(0, 0, AT, AT); }
  for (const c of [aoC, shadeC]) if (c) { c.fillStyle = '#fff'; c.fillRect(0, 0, AT, AT); }
  g.lineCap = 'round'; snowC.lineCap = 'round'; leafC.lineCap = 'round'; aoC.lineCap = 'round';
  const pick = (cols) => cols[Math.floor(R() * cols.length)];
  const clip = (ctx, r) => { ctx.save(); ctx.beginPath(); ctx.rect(r[0], r[1], r[2], r[3]); ctx.clip(); };

  // --- needle brush along a shoot (pine: long paired needles, spruce: short dense comb)
  function needles(x0, y0, ang, len, o) {
    const steps = Math.max(4, Math.floor(len / o.step));
    for (let k = 0; k < steps; k++) {
      const t = k / steps, px = x0 + Math.cos(ang) * len * t, py = y0 + Math.sin(ang) * len * t;
      const nl = o.len * (o.taper ? lerp(1, 0.55, t * t) : 1) * (0.8 + R() * 0.4);
      for (const side of [-1, 1]) {
        const a = ang + side * (o.spread * (0.75 + R() * 0.5)) * lerp(1, o.tipClose, t);
        g.strokeStyle = pick(t > 0.75 ? o.tip : o.cols);
        g.lineWidth = o.w * (0.8 + R() * 0.4);
        g.beginPath(); g.moveTo(px, py);
        const mx = px + Math.cos(a) * nl * 0.5, my = py + Math.sin(a) * nl * 0.5;
        const a2 = a + side * (R() - 0.3) * 0.25;
        g.quadraticCurveTo(mx, my, px + Math.cos(a2) * nl, py + Math.sin(a2) * nl);
        g.stroke();
      }
    }
    // tip bud / leader needles pointing forward
    const ex = x0 + Math.cos(ang) * len, ey = y0 + Math.sin(ang) * len;
    for (let k = 0; k < o.tipN; k++) {
      const a = ang + (R() - 0.5) * 0.9, nl = o.len * (0.5 + R() * 0.35);
      g.strokeStyle = pick(o.tip); g.lineWidth = o.w;
      g.beginPath(); g.moveTo(ex, ey); g.lineTo(ex + Math.cos(a) * nl, ey + Math.sin(a) * nl); g.stroke();
    }
    // snow settles along the shoot (thick soft line), AO darker along the woody axis
    snowC.strokeStyle = '#fff'; snowC.lineWidth = o.snowW; snowC.globalAlpha = o.snowA;
    snowC.beginPath(); snowC.moveTo(x0, y0); snowC.lineTo(ex, ey); snowC.stroke(); snowC.globalAlpha = 1;
    aoC.strokeStyle = 'rgba(0,0,0,0.35)'; aoC.lineWidth = o.len * 0.5;
    aoC.beginPath(); aoC.moveTo(x0, y0); aoC.lineTo(x0 + Math.cos(ang) * len * 0.5, y0 + Math.sin(ang) * len * 0.5); aoC.stroke();
  }
  const twig = (x0, y0, x1, y1, w, col) => { g.strokeStyle = col; g.lineWidth = w; g.beginPath(); g.moveTo(x0, y0); g.lineTo(x1, y1); g.stroke(); };

  // --- PINE tuft: a twig with 4-6 shoots fanning out at the end, each a brush of paired needles
  {
    const r = REG.pine; clip(g, r); clip(snowC, r); clip(aoC, r);
    const cy = r[1] + r[3] / 2, x0 = r[0] + 10;
    const cols = ['#1a2520', '#1f2b24', '#243128', '#29362c', '#2e3c30'], tip = ['#35442f', '#3b4a33', '#43533a']; // winter pine: dark grey-green (Drone 4/22)
    // the tuft fills the card: an oval brush ~90 % of the length and ~85 % of the width (pine needles 4-6 cm on a
    // 0.8-1.1 m card), denser and wider toward the tip where this year's shoots fan out
    const o = { step: 2.6, len: 66, spread: 0.95, tipClose: 0.5, taper: true, w: 2.6, cols, tip, tipN: 9, snowW: 70, snowA: 0.95 };
    twig(x0, cy, r[0] + r[2] * 0.6, cy + 4, 8, '#3d2c22');
    const bx = r[0] + r[2] * 0.5, by = cy + 3;
    needles(x0 + 24, cy, 0.0, r[2] * 0.48, { ...o, len: 58, spread: 1.15, tipN: 0 }); // older needles along the base twig
    for (const [a, l, s] of [[-1.15, 0.2, 0.25], [1.1, 0.21, 0.3], [-0.85, 0.3, 0.55], [0.9, 0.29, 0.6], [-0.45, 0.4, 0.8], [0.5, 0.38, 0.82], [0.0, 0.46, 0.9], [-0.2, 0.42, 1.0], [0.22, 0.43, 1.0]]) {
      const sx = lerp(x0 + 30, bx, s), sy = lerp(cy, by, s), ang = a * 0.95 + (R() - 0.5) * 0.12;
      needles(sx, sy, ang, r[2] * l, o);
    }
    g.restore(); snowC.restore(); aoC.restore();
  }
  // --- SPRUCE spray: a flat frond, side shoots at ~55 deg, short dense needles
  {
    const r = REG.spruce; clip(g, r); clip(snowC, r); clip(aoC, r);
    const cy = r[1] + r[3] / 2, x0 = r[0] + 8, x1 = r[0] + r[2] - 14;
    const cols = ['#152018', '#18241b', '#1c2a20', '#213024', '#253527'], tip = ['#2c3e2c', '#334632', '#3a4f36'];
    twig(x0, cy, x1, cy, 5, '#3a2b22');
    const o = { step: 2.2, len: 15, spread: 1.05, tipClose: 0.7, taper: false, w: 1.8, cols, tip, tipN: 3, snowW: 26, snowA: 0.75 };
    needles(x0, cy, 0, x1 - x0, { ...o, len: 18 });
    for (let k = 0; k < 22; k++) {
      const t = 0.04 + k * 0.043, sx = lerp(x0, x1, t);
      const L = r[3] * 0.44 * Math.pow(1 - t, 0.75) * (0.8 + R() * 0.35) + 10;
      for (const side of [-1, 1]) {
        const ang = side * (0.95 + R() * 0.2);
        needles(sx, cy, ang, L, o);
        if (L > 60) needles(sx + Math.cos(ang) * L * 0.45, cy + Math.sin(ang) * L * 0.45, ang - side * 0.75, L * 0.38, o); // secondary shoots
      }
    }
    g.restore(); snowC.restore(); aoC.restore();
  }
  // --- BIRCH twig fan (side view, base at the bottom), fine twigs (purple-brown; 'fine': grey-brown) + leaf mask on the twig ends
  if (FINE) { // WS-76 birch round 3: three shoots up the card, side twigs at an even spacing, each forking 3 times
    const r = REG.birch; clip(g, r); clip(leafC, r); clip(snowC, r); if (shadeC) clip(shadeC, r);
    const cols = ['#685a50', '#73645b', '#7e6f66', '#887970']; // neutral grey-brown, a hint of purple-red (owner 2026-10-18: the earlier mauve read lilac from afar; the blue sky light adds the rest)
    // WS-76 birch leaves (opt.birch.leaves = { size, density }): small rounded leaf clusters at the twig tips and along the
    // last two twig orders, inside a rounded envelope (the card's rectangle never reads), with gaps. Mask value = the
    // cluster's rank (which leaves a thinner season drops first), aux A = its shade. Own random stream: the twigs, the
    // bark and so the winter look stay the same pixel for pixel.
    const leafHq = LV ? (x, y, k) => {
      const u = (x - r[0]) / r[2] - 0.5, v = (y - r[1]) / r[3] - 0.56, e = clamp01((1 - Math.hypot(u / (0.47 * (LV.spread ?? 1)), v / 0.44)) * 3.5);
      if (RL() > (LV.density ?? 1) * k * e) return;
      const rank = 0.28 + 0.72 * Math.pow(RL(), 1.3), sh = (0.5 + 0.5 * RL()) * (0.8 + 0.2 * e), s = LV.size ?? 1;
      for (let n = 2 + (RL() < 0.5 ? 1 : 0), i = 0; i < n; i++) {
        const a = RL() * 6.3, d = (2 + RL() * 4) * s, lx = x + Math.cos(a) * d, ly = y + Math.sin(a) * d, rx = (3.2 + RL() * 2.3) * s, ry = (2.2 + RL() * 1.4) * s, rot = a + (RL() - 0.5);
        const g8 = Math.round(255 * rank), s8 = Math.round(255 * clamp01(sh * (0.85 + 0.3 * RL())));
        leafC.fillStyle = `rgb(${g8},${g8},${g8})`; leafC.beginPath(); leafC.ellipse(lx, ly, rx, ry, rot, 0, 6.3); leafC.fill();
        shadeC.fillStyle = `rgb(${s8},${s8},${s8})`; shadeC.beginPath(); shadeC.ellipse(lx, ly, rx, ry, rot, 0, 6.3); shadeC.fill();
      }
    } : null;
    const leaf = (x, y) => { // today's blobs; with leaves on the same random numbers are drawn (the twigs stay identical), the clusters drawn instead
      const al = R(), b = [0, 1].map(() => [R(), R(), R(), R(), R()]);
      if (leafHq) { leafHq(x, y, 1); return; }
      leafC.fillStyle = `rgba(255,255,255,${0.75 + al * 0.25})`; for (const [p, q, s, t, w] of b) { leafC.beginPath(); leafC.ellipse(x + (p - 0.5) * 10, y + (q - 0.5) * 10, 5 + s * 4, 3 + t * 3, w * 3, 0, 6.3); leafC.fill(); }
    };
    const line = (x, y, x1, y1, w, bend) => {
      g.strokeStyle = pick(cols); g.lineWidth = Math.max(1.2, w);
      g.beginPath(); g.moveTo(x, y); g.quadraticCurveTo((x + x1) / 2 + bend, (y + y1) / 2, x1, y1); g.stroke();
      snowC.strokeStyle = '#fff'; snowC.lineWidth = Math.max(2, w * 2.2); snowC.globalAlpha = 0.5;
      snowC.beginPath(); snowC.moveTo(x, y); snowC.lineTo(x1, y1); snowC.stroke(); snowC.globalAlpha = 1;
    };
    const tw = (x, y, a, len, w, depth) => {
      const x1 = x + Math.cos(a) * len, y1 = y + Math.sin(a) * len;
      line(x, y, x1, y1, w, (R() - 0.5) * len * 0.12);
      if (leafHq && depth <= 1) leafHq((x + x1) / 2, (y + y1) / 2, 0.55); // WS-76 leaves along the last two twig orders
      if (depth === 0) { leaf(x1, y1); return; }
      for (const s of [-1, 1]) tw(x1, y1, a + s * (0.3 + R() * 0.22) + (Math.PI / 2 - a) * 0.15, len * (0.58 + R() * 0.14), w * 0.72, depth - 1);
    };
    const bx = r[0] + r[2] / 2, by = r[1] + 6, top = r[3] - 24;
    for (const da of [-0.62, -0.3, 0, 0.3, 0.62]) {
      const a = Math.PI / 2 + da + (R() - 0.5) * 0.08, L = top * (1 - Math.abs(da) * 0.3), ax = Math.cos(a), ay = Math.sin(a);
      line(bx, by, bx + ax * L, by + ay * L, 2.6, 0);
      for (let t = 0.08, s = da < 0 ? -1 : 1; t < 0.96; t += 0.06 + R() * 0.02, s = -s) {
        const env = Math.pow(Math.sin(Math.PI * Math.min(1, t * 1.08)), 0.6);
        tw(bx + ax * L * t, by + ay * L * t, a + s * (0.55 + R() * 0.25), (24 + 50 * env) * (0.85 + R() * 0.3), 1.9, 4);
      }
      leaf(bx + ax * L, by + ay * L);
    }
    g.restore(); leafC.restore(); snowC.restore(); shadeC?.restore();
  } else {
    const r = REG.birch; clip(g, r); clip(leafC, r); clip(snowC, r);
    const cols = ['#4b3b3f', '#57464a', '#625055', '#6e5b5f'];
    const branch = (x, y, a, len, w, depth) => {
      if (depth === 0 || len < 4) {
        leafC.fillStyle = `rgba(255,255,255,${0.75 + R() * 0.25})`;
        for (let k = 0; k < 3; k++) { leafC.beginPath(); leafC.ellipse(x + (R() - 0.5) * 14, y + (R() - 0.5) * 14, 6 + R() * 5, 4 + R() * 3, R() * 3, 0, 6.3); leafC.fill(); }
        return;
      }
      const x1 = x + Math.cos(a) * len, y1 = y + Math.sin(a) * len;
      g.strokeStyle = pick(cols); g.lineWidth = Math.max(0.9, w);
      g.beginPath(); g.moveTo(x, y); g.quadraticCurveTo(x + Math.cos(a + 0.15) * len * 0.5, y + Math.sin(a + 0.15) * len * 0.5, x1, y1); g.stroke();
      snowC.strokeStyle = '#fff'; snowC.lineWidth = Math.max(2, w * 2.2); snowC.globalAlpha = 0.55;
      snowC.beginPath(); snowC.moveTo(x, y); snowC.lineTo(x1, y1); snowC.stroke(); snowC.globalAlpha = 1;
      const n = depth > 4 ? 2 : 3;
      for (let k = 0; k < n; k++) branch(x1, y1, a + (R() - 0.5) * 0.95 + (Math.PI / 2 - a) * 0.18, len * (0.62 + R() * 0.16), w * 0.7, depth - 1);
    };
    const bx = r[0] + r[2] / 2, by = r[1] + 6;
    for (let k = 0; k < 9; k++) branch(bx + (R() - 0.5) * 80, by, Math.PI / 2 + (R() - 0.5) * 1.5, 80 + R() * 40, 3.2, 7); // canvas y down = up the card
    g.restore(); leafC.restore(); snowC.restore();
  }
  // --- bark strips (v0 = ground at canvas top of the strip)
  function bark(r, low, high, split, marks) {
    const grd = g.createLinearGradient(0, r[1], 0, r[1] + r[3]);
    low.forEach((c, i) => grd.addColorStop((i / (low.length - 1)) * split, c));
    high.forEach((c, i) => grd.addColorStop(split + (i / (high.length - 1)) * (1 - split), c));
    g.fillStyle = grd; g.fillRect(r[0], r[1], r[2], r[3]);
    marks(r);
  }
  bark(REG.pineBark, ['#3e352f', '#4a3f37', '#56483d'], ['#7a5238', '#8f5e3f', '#9c6846', '#a46f4b'], 0.42, (r) => {
    for (let k = 0; k < 260; k++) { // plates / furrows: grey-brown plates low, orange flakes high
      const y = r[1] + R() * r[3], hi = (y - r[1]) / r[3] > 0.45;
      g.fillStyle = hi ? `rgba(${170 + R() * 30},${110 + R() * 25},${72 + R() * 20},0.4)` : `rgba(30,24,20,${0.35 + R() * 0.3})`;
      g.fillRect(r[0] + R() * r[2], y, hi ? 6 + R() * 10 : 2 + R() * 3, hi ? 3 + R() * 4 : 10 + R() * 18);
    }
  });
  // base = dark grey furrowed bark, never near-black (user 2026-10-13: the trunk feet read as black socks on the snow)
  // WS-76 birch round 3 (opt.birch.foot): a short grey-brown foot, the lenticels from just above it
  const foot = OB.foot, m0 = foot ?? 0.12;
  bark(REG.birchBark, foot != null ? ['#6a6158', '#7d746a', '#b0a99f'] : ['#4a4440', '#5d5650', '#9a938a'], ['#e4e0d7', '#ece9e2', '#efece6'], foot ?? 0.14, (r) => {
    for (let k = 0; k < 160; k++) { // lenticels: thin dark horizontal dashes, black diamonds near branch scars
      const y = r[1] + r[3] * (foot != null ? m0 + R() * (1 - m0) : 0.12 + R() * 0.88);
      g.fillStyle = `rgba(35,30,30,${0.5 + R() * 0.4})`; g.fillRect(r[0] + R() * r[2], y, 6 + R() * 16, 1.4 + R() * 1.6);
      if (R() < 0.12) { g.fillStyle = 'rgba(25,22,22,0.8)'; g.beginPath(); const x = r[0] + R() * r[2]; g.moveTo(x, y - 5); g.lineTo(x + 7, y); g.lineTo(x, y + 5); g.lineTo(x - 7, y); g.fill(); }
      if (R() < 0.3) { g.fillStyle = `rgba(170,165,155,${0.3 + R() * 0.3})`; g.fillRect(r[0] + R() * r[2], y, 12 + R() * 20, 4 + R() * 6); }
    }
  });
  bark(REG.spruceBark, ['#3a302a', '#463a32'], ['#5a4a3e', '#6a5646'], 0.5, (r) => {
    for (let k = 0; k < 80; k++) { g.fillStyle = `rgba(30,25,22,${0.3 + R() * 0.3})`; g.fillRect(r[0] + R() * r[2], r[1] + R() * r[3], 2 + R() * 6, 2 + R() * 6); }
  });
  { // dark needle interior
    const r = REG.core; g.fillStyle = '#111a14'; g.fillRect(r[0], r[1], r[2], r[3]);
    for (let k = 0; k < 900; k++) { g.strokeStyle = pick(['#18241b', '#1d2b20', '#0f1611']); g.lineWidth = 1.4; const x = r[0] + R() * r[2], y = r[1] + R() * r[3], a = R() * 6.3; g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(a) * 9, y + Math.sin(a) * 9); g.stroke(); }
    snowC.fillStyle = 'rgba(255,255,255,0.25)'; snowC.fillRect(r[0], r[1], r[2], r[3]);
  }
  for (const k of ['solid', 'solidFlat']) { const r = REG[k]; g.fillStyle = '#fff'; g.fillRect(r[0], r[1], r[2], r[3]); }
  { // stylised pads: soft blotchy snow mask so caps end in an irregular soft edge
    const r = REG.solid; snowC.fillStyle = 'rgba(255,255,255,0.55)'; snowC.fillRect(r[0], r[1], r[2], r[3]);
    for (let k = 0; k < 40; k++) { snowC.fillStyle = `rgba(255,255,255,${0.2 + R() * 0.3})`; snowC.beginPath(); snowC.arc(r[0] + R() * r[2], r[1] + R() * r[3], 4 + R() * 10, 0, 6.3); snowC.fill(); }
  }
  // snow on top of bark (thick branches carry a little). The whole strip, down to the ground: the mask used to start
  // 15 % up, so at a high snow load the trunk frosted over and its foot stayed bare bark with a hard edge.
  for (const k of ['pineBark', 'birchBark', 'spruceBark']) { const r = REG[k]; snowC.fillStyle = 'rgba(255,255,255,0.5)'; snowC.fillRect(r[0], r[1], r[2], r[3]); }

  // --- assemble: albedo with colour bled into transparent texels (no dark fringes in the mips)
  const img = g.getImageData(0, 0, AT, AT), d = img.data;
  const fill = { pine: [40, 52, 36], spruce: [27, 38, 30], birch: FINE ? [124, 100, 104] : [92, 76, 80] };
  const regionFill = (x, y) => (x < 512 && y < 512 ? fill.pine : x >= 512 && y < 512 ? fill.spruce : fill.birch);
  for (let y = 0; y < AT; y++) for (let x = 0; x < AT; x++) {
    const i = (y * AT + x) * 4;
    if (d[i + 3] < 8) { const f = regionFill(x, y); d[i] = f[0]; d[i + 1] = f[1]; d[i + 2] = f[2]; d[i + 3] = 0; }
    else if (d[i + 3] < 255) { d[i + 3] = Math.min(255, d[i + 3] * 1.25); } // anti-aliased needle edges: keep their coverage
  }
  const S = AUX; // aux maps are AUX px (the masks are soft: 4x cheaper than full size)
  const down = (ctx) => { const c = newCanvas(S, S), x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(ctx.canvas, 0, 0, S, S); return x.getImageData(0, 0, S, S).data; };
  const blur = (src, rad, passes = 3) => { // separable box blur on one channel (Uint8 RGBA source, R channel)
    let a = new Float32Array(S * S); for (let i = 0; i < S * S; i++) a[i] = src[i * 4] / 255;
    let b = new Float32Array(S * S);
    for (let p = 0; p < passes; p++) {
      for (let y = 0; y < S; y++) { let s = 0; const row = y * S; for (let x = -rad; x <= rad; x++) s += a[row + Math.min(S - 1, Math.max(0, x))]; for (let x = 0; x < S; x++) { b[row + x] = s / (2 * rad + 1); s += a[row + Math.min(S - 1, x + rad + 1)] - a[row + Math.max(0, x - rad)]; } }
      for (let x = 0; x < S; x++) { let s = 0; for (let y = -rad; y <= rad; y++) s += b[Math.min(S - 1, Math.max(0, y)) * S + x]; for (let y = 0; y < S; y++) { a[y * S + x] = s / (2 * rad + 1); s += b[Math.min(S - 1, y + rad + 1) * S + x] - b[Math.max(0, y - rad) * S + x]; } }
    }
    return a;
  };
  const sn = blur(down(snowC), 5), lf = blur(down(leafC), 1, 1), ao = blur(down(aoC), 3), sh = shadeC ? blur(down(shadeC), 1, 1) : null;
  const lfK = LV ? 1 : 1.4; // WS-76 leaves: the mask holds ranks (no boost, which would flatten them)
  // snow lumps: break the settle mask with blobs, so loads > 0.5 grow lumpy caps, not stripes
  const aux = new Uint8Array(S * S * 4), k = AT / S;
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
    const i = y * S + x;
    const lump = 0.6 + 0.4 * Math.sin(x * k * 0.11 + Math.sin(y * k * 0.07) * 2.0) * Math.sin(y * k * 0.13 + Math.sin(x * k * 0.05) * 1.7);
    aux[i * 4] = Math.round(255 * clamp01(sn[i] * 1.55 * (0.5 + 0.5 * lump)));
    aux[i * 4 + 1] = Math.round(255 * clamp01(lf[i] * lfK));
    aux[i * 4 + 2] = Math.round(255 * clamp01(ao[i]));
    aux[i * 4 + 3] = sh ? Math.round(255 * clamp01(sh[i])) : 255; // WS-76 leaves: the leaf shade (else unused, 1)
  }
  return { map: new Uint8Array(d.buffer.slice(0)), aux, size: AT, auxSize: S };
}
